import { useEffect, useState } from "react";
import API, { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { Label } from "../../components/ui/label";
import { PhoneInput } from "../../components/PhoneInput";
import { getCountryCallingCode, isValidPhoneNumber } from "libphonenumber-js";
import { format } from "date-fns";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Copy, CalendarIcon, Mail, Link2Off, MapPin, Pencil, XCircle } from "lucide-react";

const combineDateTime = (date, time) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}T${time || "00:00"}`;
};

const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString();

const formatScheduleRange = (start, end) => {
  if (!start) return null;
  const startDt = new Date(start);
  const label = startDt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const startTime = startDt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (!end) return `${label} · ${startTime}`;
  const endTime = new Date(end).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${label} · ${startTime} – ${endTime}`;
};

const EMAIL_ERROR_MESSAGES = {
  not_connected: "Invite created, but the guest email wasn't sent — connect your Microsoft account to send it from your inbox.",
  reconnect_required: "Invite created, but your Microsoft connection expired — reconnect to send the guest email.",
  no_mailbox: "Invite created, but this Microsoft account has no Outlook mailbox — connect a licensed O365 account to send guest emails.",
  send_failed: "Invite created, but sending the guest email failed. Please try again.",
};

const CALENDAR_ERROR_MESSAGES = {
  not_connected: "Your calendar wasn't blocked — connect your Microsoft account to auto-block your calendar for visits.",
  reconnect_required: "Your calendar wasn't blocked — reconnect your Microsoft account to grant calendar access.",
  calendar_failed: "Your calendar wasn't blocked for this visit. Please add it manually.",
};

export default function PreRegister() {
  const { user, refetch } = useAuth();
  const [purposes, setPurposes] = useState([]);
  const [gates, setGates] = useState([]);
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ visitor_name: "", visitor_email: "", visitor_phone: "", visitor_company: "", purpose: "", gate_id: "", scheduled_start: "", scheduled_end: "" });
  const [error, setError] = useState("");
  const [scheduledDate, setScheduledDate] = useState(undefined);
  const [fromTimeStr, setFromTimeStr] = useState("");
  const [toTimeStr, setToTimeStr] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("IN");
  const [phoneNational, setPhoneNational] = useState("");
  const [msAccount, setMsAccount] = useState({ connected: false, email: null, configured: true });
  const [plantPickerValue, setPlantPickerValue] = useState("");
  const [savingPlant, setSavingPlant] = useState(false);
  const [editingInvite, setEditingInvite] = useState(null);
  const [editDate, setEditDate] = useState(undefined);
  const [editFrom, setEditFrom] = useState("");
  const [editTo, setEditTo] = useState("");
  const [editError, setEditError] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const loadMsStatus = async () => {
    const { data } = await api.get("/auth/microsoft/status");
    setMsAccount(data);
  };

  const load = async () => {
    const [p, i, g] = await Promise.all([api.get("/purposes"), api.get("/invites"), api.get("/gates")]);
    setPurposes(p.data);
    setInvites(i.data);
    setGates(g.data);
  };

  const openEdit = (inv) => {
    const start = inv.scheduled_start ? new Date(inv.scheduled_start) : null;
    const end = inv.scheduled_end ? new Date(inv.scheduled_end) : null;
    setEditDate(start ? new Date(start.getFullYear(), start.getMonth(), start.getDate()) : undefined);
    setEditFrom(start ? `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}` : "");
    setEditTo(end ? `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}` : "");
    setEditError("");
    setEditingInvite(inv);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editDate || !editFrom || !editTo) {
      setEditError("Date, start time, and end time are all required");
      return;
    }
    const newStart = combineDateTime(editDate, editFrom);
    const newEnd = combineDateTime(editDate, editTo);
    if (newEnd <= newStart) {
      setEditError("End time must be after start time");
      return;
    }
    setEditSaving(true);
    setEditError("");
    try {
      const { data } = await api.put(`/invites/${editingInvite.id}`, { scheduled_start: newStart, scheduled_end: newEnd });
      if (data.calendar_error) toast.error("Invite time updated, but your calendar event couldn't be updated automatically");
      else toast.success("Invite rescheduled");
      setEditingInvite(null);
      load();
    } catch (err) {
      setEditError(formatApiError(err.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  const cancelInvite = async (inv) => {
    try {
      await api.post(`/invites/${inv.id}/cancel`);
      toast.success(`Invite for ${inv.visitor_name} cancelled — calendar hold released`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    loadMsStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get("ms_connected")) {
      toast.success("Microsoft account connected");
      loadMsStatus();
    } else if (params.get("ms_error")) {
      toast.error("Could not connect Microsoft account — please try again");
    }
    if (params.get("ms_connected") || params.get("ms_error")) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (user?.gate_id) setForm((f) => ({ ...f, gate_id: f.gate_id || user.gate_id }));
  }, [user?.gate_id]);

  const disconnectMs = async () => {
    await api.post("/auth/microsoft/disconnect");
    toast.success("Microsoft account disconnected");
    setMsAccount({ connected: false, email: null });
  };

  const selectPlant = async (gateId) => {
    if (!gateId) return;
    setSavingPlant(true);
    try {
      await api.put("/users/me/plant", { gate_id: gateId });
      await refetch();
      setForm((f) => ({ ...f, gate_id: gateId }));
      toast.success("Plant updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPlant(false);
    }
  };

  const handlePhoneCountryChange = (c) => {
    setPhoneCountry(c);
    setForm({ ...form, visitor_phone: phoneNational ? `+${getCountryCallingCode(c)}${phoneNational}` : "" });
  };

  const handlePhoneNationalChange = (digits) => {
    setPhoneNational(digits);
    setForm({ ...form, visitor_phone: digits ? `+${getCountryCallingCode(phoneCountry)}${digits}` : "" });
  };

  const handleDateSelect = (date) => {
    setScheduledDate(date);
    setForm({ ...form, scheduled_start: combineDateTime(date, fromTimeStr), scheduled_end: combineDateTime(date, toTimeStr) });
  };

  const handleFromTimeChange = (e) => {
    const value = e.target.value;
    setFromTimeStr(value);
    setForm({ ...form, scheduled_start: combineDateTime(scheduledDate, value) });
  };

  const handleToTimeChange = (e) => {
    const value = e.target.value;
    setToTimeStr(value);
    setForm({ ...form, scheduled_end: combineDateTime(scheduledDate, value) });
  };

  const submit = async (e) => {
    e.preventDefault();
    const phone = form.visitor_phone.trim();
    const email = form.visitor_email.trim();
    if (!phone && !email) {
      setError("Enter a phone number or an email — at least one is required");
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email address");
      return;
    }
    if (phone && !isValidPhoneNumber(phone)) {
      setError("Enter a valid phone number for the selected country");
      return;
    }
    if (form.scheduled_start && new Date(form.scheduled_start) < new Date()) {
      setError("Scheduled date/time cannot be in the past");
      return;
    }
    if (form.scheduled_start && !form.scheduled_end) {
      setError("Enter an end time for the visit");
      return;
    }
    if (form.scheduled_end && form.scheduled_end <= form.scheduled_start) {
      setError("End time must be after the start time");
      return;
    }
    setError("");
    try {
      const { data } = await api.post("/invites", form);
      const messages = [];
      if (data.email_sent) messages.push("guest email sent via your Outlook");
      else if (data.email_error) toast.error(EMAIL_ERROR_MESSAGES[data.email_error] || EMAIL_ERROR_MESSAGES.send_failed);
      if (data.calendar_created) messages.push("your calendar blocked for this visit");
      else if (data.calendar_error) toast.error(CALENDAR_ERROR_MESSAGES[data.calendar_error] || CALENDAR_ERROR_MESSAGES.calendar_failed);
      if (messages.length) toast.success(`Invite created — ${messages.join(" and ")}. Fallback code: ${data.short_code}`, { duration: 8000 });
      else if (!data.email_error && !data.calendar_error) toast.success(`Invite created — fallback code: ${data.short_code}`, { duration: 8000 });
      navigator.clipboard?.writeText(data.link).catch(() => {});
      setForm({ visitor_name: "", visitor_email: "", visitor_phone: "", visitor_company: "", purpose: "", gate_id: user?.gate_id || "", scheduled_start: "", scheduled_end: "" });
      setScheduledDate(undefined);
      setFromTimeStr("");
      setToTimeStr("");
      setPhoneNational("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const currentGate = gates.find((g) => g.id === user?.gate_id);

  if (user && user.role !== "admin" && !user.gate_id) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[70vh]" data-testid="plant-onboarding-page">
        <div className="max-w-md w-full border border-slate-200 bg-white p-8 text-center space-y-5">
          <div className="w-14 h-14 rounded-full bg-brand-cyan/10 text-brand-cyan flex items-center justify-center mx-auto">
            <MapPin size={26} />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan">One-Time Setup</span>
            <h1 className="text-2xl font-bold uppercase tracking-tight mt-1 text-brand-navy">Select Your Plant</h1>
            <p className="text-sm text-slate-500 mt-2">Choose the plant you're based at. You can change this anytime.</p>
          </div>
          <Select value={plantPickerValue} onValueChange={setPlantPickerValue}>
            <SelectTrigger data-testid="plant-onboarding-select" className="h-12 rounded-sm bg-white border-slate-300">
              <SelectValue placeholder="Choose a plant" />
            </SelectTrigger>
            <SelectContent>
              {gates.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => selectPlant(plantPickerValue)}
            disabled={!plantPickerValue || savingPlant}
            data-testid="plant-onboarding-continue"
            className="w-full h-12 rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-sm"
          >
            {savingPlant ? "Saving..." : "Continue"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="pre-register-page">
      <div>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan">Hosting</span>
        <h1 className="text-3xl font-bold uppercase tracking-tight mt-1 text-brand-navy">Pre-Register Visitor</h1>
      </div>
      <div className="border border-slate-200 bg-white p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 max-w-lg" data-testid="plant-context-banner">
        <div className="flex items-center gap-3">
          <MapPin size={20} className="text-brand-cyan shrink-0" />
          <div>
            <p className="text-sm font-bold text-brand-navy" data-testid="plant-context-name">
              {currentGate?.name || "No plant set"}
            </p>
            <p className="text-xs text-slate-500">{user?.multi_plant_access ? "Your home plant — pick any plant per invite below" : "Your assigned plant"}</p>
          </div>
        </div>
        <Select value="" onValueChange={selectPlant}>
          <SelectTrigger data-testid="plant-change-trigger" className="w-full sm:w-32 rounded-sm bg-white border-slate-300 text-xs uppercase tracking-widest">
            <SelectValue placeholder="Change" />
          </SelectTrigger>
          <SelectContent>
            {gates.map((g) => (
              <SelectItem key={g.id} value={g.id}>
                {g.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div
        className="border border-slate-200 bg-white p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 max-w-lg"
        data-testid="microsoft-account-banner"
      >
        <div className="flex items-center gap-3">
          <Mail size={20} className={msAccount.connected ? "text-brand-cyan shrink-0" : "text-slate-400 shrink-0"} />
          <div>
            <p className="text-sm font-bold text-brand-navy">
              {msAccount.connected ? "Connected to Microsoft" : "Send invites & auto-block your calendar"}
            </p>
            <p className="text-xs text-slate-500" data-testid="microsoft-account-email">
              {msAccount.connected ? msAccount.email : msAccount.configured ? "Not connected yet" : "Not set up by your admin yet"}
            </p>
          </div>
        </div>
        {msAccount.connected ? (
          <Button
            type="button"
            variant="outline"
            onClick={disconnectMs}
            data-testid="microsoft-disconnect-button"
            className="w-full sm:w-auto rounded-sm border-slate-300 text-slate-500 hover:text-red-600 gap-2"
          >
            <Link2Off size={16} /> Disconnect
          </Button>
        ) : msAccount.configured ? (
          <Button
            type="button"
            onClick={() => (window.location.href = `${API}/auth/microsoft/connect`)}
            data-testid="microsoft-connect-button"
            className="w-full sm:w-auto rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light"
          >
            Connect Microsoft Account
          </Button>
        ) : (
          <span className="text-xs text-slate-400 italic" data-testid="microsoft-not-configured">
            Pending admin setup
          </span>
        )}
      </div>
      <form onSubmit={submit} className="border border-slate-200 bg-white p-6 space-y-4 max-w-lg">
        <Input
          data-testid="invite-name-input"
          placeholder="Visitor name"
          required
          value={form.visitor_name}
          onChange={(e) => setForm({ ...form, visitor_name: e.target.value })}
          className="rounded-sm bg-white border-slate-300"
        />
        <Input
          data-testid="invite-email-input"
          placeholder="Visitor email"
          type="text"
          inputMode="email"
          value={form.visitor_email}
          onChange={(e) => setForm({ ...form, visitor_email: e.target.value })}
          className="rounded-sm bg-white border-slate-300"
        />
        <PhoneInput
          testId="invite-phone-input"
          country={phoneCountry}
          national={phoneNational}
          onCountryChange={handlePhoneCountryChange}
          onNationalChange={handlePhoneNationalChange}
        />
        <p className="text-xs text-slate-400 -mt-2">Provide at least a phone number or an email</p>
        <Input
          data-testid="invite-company-input"
          placeholder="Company"
          value={form.visitor_company}
          onChange={(e) => setForm({ ...form, visitor_company: e.target.value })}
          className="rounded-sm bg-white border-slate-300"
        />
        <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
          <SelectTrigger data-testid="invite-purpose-select" className="rounded-sm bg-white border-slate-300">
            <SelectValue placeholder="Purpose of visit" />
          </SelectTrigger>
          <SelectContent>
            {purposes.map((p) => (
              <SelectItem key={p.id} value={p.name}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {user?.multi_plant_access && (
          <Select value={form.gate_id} onValueChange={(v) => setForm({ ...form, gate_id: v })}>
            <SelectTrigger data-testid="invite-plant-select" className="rounded-sm bg-white border-slate-300">
              <SelectValue placeholder="Plant for this visit" />
            </SelectTrigger>
            <SelectContent>
              {gates.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="space-y-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                data-testid="invite-scheduled-date-trigger"
                className={`w-full justify-start text-left font-normal rounded-sm border-slate-300 ${scheduledDate ? "text-brand-navy" : "text-slate-400"}`}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {scheduledDate ? format(scheduledDate, "PPP") : "Scheduled date (optional)"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" data-testid="invite-scheduled-calendar-popover">
              <Calendar mode="single" selected={scheduledDate} onSelect={handleDateSelect} disabled={(date) => date < new Date().setHours(0, 0, 0, 0)} initialFocus />
            </PopoverContent>
          </Popover>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <Label className="text-xs text-slate-400">From</Label>
              <Input
                data-testid="invite-scheduled-from-time-input"
                type="time"
                value={fromTimeStr}
                onChange={handleFromTimeChange}
                disabled={!scheduledDate}
                min={isSameDay(scheduledDate, new Date()) ? new Date().toTimeString().slice(0, 5) : undefined}
                className="rounded-sm bg-white border-slate-300 w-full"
              />
            </div>
            <div className="flex-1">
              <Label className="text-xs text-slate-400">To</Label>
              <Input
                data-testid="invite-scheduled-to-time-input"
                type="time"
                value={toTimeStr}
                onChange={handleToTimeChange}
                disabled={!fromTimeStr}
                min={fromTimeStr || undefined}
                className="rounded-sm bg-white border-slate-300 w-full"
              />
            </div>
          </div>
          <p className="text-xs text-slate-400">Blocks your Outlook calendar for this visit if connected</p>
        </div>
        {error && (
          <p className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm" data-testid="invite-form-error">
            {error}
          </p>
        )}
        <Button type="submit" data-testid="invite-submit-button" className="w-full rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-sm">
          Send Invite
        </Button>
      </form>

      <div className="border border-slate-200 bg-white">
        <div className="p-4 border-b border-slate-200 text-xs uppercase tracking-widest text-slate-400">My Invites</div>
        {invites.map((inv) => (
          <div key={inv.id} data-testid={`invite-row-${inv.id}`} className="flex items-center justify-between p-4 border-b border-slate-100">
            <div>
              <p className="font-bold text-brand-navy">{inv.visitor_name}</p>
              <p className="text-sm text-slate-500">
                {inv.purpose} · <span className="uppercase">{inv.status}</span>
              </p>
              {inv.short_code && (
                <p className="text-xs text-slate-400 mt-1" data-testid={`invite-code-${inv.id}`}>
                  Fallback code: <span className="font-mono text-brand-navy">{inv.short_code}</span>
                </p>
              )}
              {inv.scheduled_start && (
                <p className="text-xs text-slate-400 mt-1" data-testid={`invite-schedule-${inv.id}`}>
                  {formatScheduleRange(inv.scheduled_start, inv.scheduled_end)}
                  {inv.calendar_event_id && <span className="text-brand-cyan"> · calendar blocked</span>}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {inv.status === "pending" && (
                <>
                  <button data-testid={`edit-invite-${inv.id}`} onClick={() => openEdit(inv)} className="text-slate-400 hover:text-brand-navy">
                    <Pencil size={18} />
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`cancel-invite-${inv.id}`} className="text-slate-400 hover:text-red-600">
                        <XCircle size={18} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-sm">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancel this invite?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will cancel the invite for <strong>{inv.visitor_name}</strong> and release your Outlook
                          calendar hold, if one was created. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`cancel-invite-dismiss-${inv.id}`}>No, keep it</AlertDialogCancel>
                        <AlertDialogAction
                          data-testid={`cancel-invite-confirm-${inv.id}`}
                          onClick={() => cancelInvite(inv)}
                          className="rounded-sm bg-red-600 text-white hover:bg-red-700"
                        >
                          Yes, Cancel Invite
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
              <button
                data-testid={`copy-invite-link-${inv.id}`}
                onClick={() => {
                  navigator.clipboard
                    ?.writeText(`${window.location.origin}/invite/${inv.token}`)
                    .then(() => toast.success("Link copied"))
                    .catch(() => toast.error("Could not copy link — clipboard access denied"));
                }}
                className="text-slate-400 hover:text-brand-navy"
              >
                <Copy size={18} />
              </button>
            </div>
          </div>
        ))}
        {!invites.length && <p className="p-6 text-center text-slate-400">No invites yet</p>}
      </div>

      <Dialog open={!!editingInvite} onOpenChange={(v) => !v && setEditingInvite(null)}>
        <DialogContent className="rounded-sm" data-testid="edit-invite-dialog">
          <DialogHeader>
            <DialogTitle className="uppercase text-brand-navy">Reschedule Invite</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4" noValidate>
            <p className="text-sm text-slate-500">{editingInvite?.visitor_name}</p>
            {editError && (
              <p className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm" data-testid="edit-invite-error">
                {editError}
              </p>
            )}
            <div className="space-y-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="edit-invite-date"
                    className={`w-full justify-start text-left font-normal rounded-sm border-slate-300 ${editDate ? "text-brand-navy" : "text-slate-400"}`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editDate ? format(editDate, "PPP") : "Select a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" data-testid="edit-invite-date-popover">
                  <Calendar mode="single" selected={editDate} onSelect={setEditDate} disabled={(date) => date < new Date().setHours(0, 0, 0, 0)} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>From</Label>
                <Input type="time" data-testid="edit-invite-from" value={editFrom} onChange={(e) => setEditFrom(e.target.value)} className="rounded-sm bg-white border-slate-300" />
              </div>
              <div className="space-y-2">
                <Label>To</Label>
                <Input type="time" data-testid="edit-invite-to" value={editTo} onChange={(e) => setEditTo(e.target.value)} className="rounded-sm bg-white border-slate-300" />
              </div>
            </div>
            <Button type="submit" disabled={editSaving} data-testid="edit-invite-save" className="w-full rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-sm">
              {editSaving ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
